import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '../../common/functions/cn';
import { ButtonV2 } from '../button-v2/button-v2';

const attachmentVariants = cva(
  [
    'group/attachment relative flex w-fit min-w-0 max-w-full shrink-0 flex-wrap',
    'rounded-md border bg-card text-card-foreground transition-colors',
    'focus-within:ring-1 focus-within:ring-ring/30',
    // Only tint on hover when the card is actually activatable.
    'has-[>a,>button]:hover:bg-muted/50',
    'data-[state=error]:border-error/30 data-[state=idle]:border-dashed',
  ],
  {
    variants: {
      size: {
        default: [
          'gap-2 text-sm',
          'has-data-[slot=attachment-content]:px-2.5 has-data-[slot=attachment-content]:py-2',
          'has-data-[slot=attachment-media]:p-2',
        ],
        sm: [
          'gap-2.5 text-xs',
          'has-data-[slot=attachment-content]:px-2 has-data-[slot=attachment-content]:py-1.5',
          'has-data-[slot=attachment-media]:p-1.5',
        ],
        xs: [
          'gap-1.5 rounded-sm text-xs',
          'has-data-[slot=attachment-content]:px-1.5 has-data-[slot=attachment-content]:py-1',
          'has-data-[slot=attachment-media]:p-1',
        ],
      },
      orientation: {
        horizontal: 'min-w-40 items-center',
        vertical: 'w-24 flex-col has-data-[slot=attachment-content]:w-30',
      },
    },
  }
);

export type AttachmentProps = ComponentProps<'div'> &
  VariantProps<typeof attachmentVariants> & {
    /**
     * The upload state. `idle` dashes the border and `error` recolours the border, media and
     * description. `uploading` and `processing` carry no styling of their own, so put the
     * in-progress affordance in the media slot. Every value is exposed as `data-state` for styling
     * from outside.
     *
     * @default "done"
     */
    state?: 'idle' | 'uploading' | 'processing' | 'error' | 'done';
  };

/**
 * The root attachment container: a file or image with its media, name, metadata and actions.
 *
 * `size` (`default` | `sm` | `xs`) and `orientation` (`horizontal` | `vertical`) come from the
 * variants. `vertical` stacks the media above the content and narrows the card.
 */
const Attachment = ({
  className,
  state = 'done',
  size = 'default',
  orientation = 'horizontal',
  ...props
}: AttachmentProps) => {
  return (
    <div
      data-slot="attachment"
      data-state={state}
      data-size={size}
      data-orientation={orientation}
      className={cn(attachmentVariants({ size, orientation }), className)}
      {...props}
    />
  );
};

const attachmentMediaVariants = cva(
  [
    'relative flex aspect-square w-10 shrink-0 items-center justify-center overflow-hidden',
    'rounded-md bg-muted text-foreground',
    'group-data-[orientation=vertical]/attachment:w-full',
    'group-data-[size=sm]/attachment:w-8',
    'group-data-[size=xs]/attachment:w-7 group-data-[size=xs]/attachment:rounded-sm',
    'group-data-[state=error]/attachment:bg-error/10 group-data-[state=error]/attachment:text-error',
    // Icons scale with the attachment rather than carrying their own size.
    "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
    "group-data-[orientation=vertical]/attachment:[&_svg:not([class*='size-'])]:size-6",
    "group-data-[size=xs]/attachment:[&_svg:not([class*='size-'])]:size-3.5",
  ],
  {
    variants: {
      variant: {
        icon: '',
        image: [
          // Dimmed until the upload settles, so a partial image reads as pending.
          'opacity-60',
          'group-data-[state=done]/attachment:opacity-100 group-data-[state=idle]/attachment:opacity-100',
          '*:[img]:aspect-square *:[img]:w-full *:[img]:object-cover',
        ],
      },
    },
    defaultVariants: {
      variant: 'icon',
    },
  }
);

export type AttachmentMediaProps = ComponentProps<'div'> & VariantProps<typeof attachmentMediaVariants>;

/**
 * The media slot, holding either an icon or an image preview.
 *
 * `variant="image"` expects a single `<img>` child, which it crops to a square. Icons are sized to
 * the attachment, so they do not need their own size class.
 */
const AttachmentMedia = ({ className, variant = 'icon', ...props }: AttachmentMediaProps) => {
  return (
    <div
      data-slot="attachment-media"
      data-variant={variant}
      className={cn(attachmentMediaVariants({ variant }), className)}
      {...props}
    />
  );
};

/** Wraps the title and description, and takes the remaining width beside the media. */
const AttachmentContent = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="attachment-content"
      className={cn(
        'min-w-0 max-w-full flex-1 leading-tight',
        'group-data-[orientation=vertical]/attachment:px-1',
        className
      )}
      {...props}
    />
  );
};

/** The attachment name. Truncates rather than wrapping, so pass the full name as a `title` too. */
const AttachmentTitle = ({ className, ...props }: ComponentProps<'span'>) => {
  return (
    <span
      data-slot="attachment-title"
      className={cn('block min-w-0 max-w-full truncate font-medium', className)}
      {...props}
    />
  );
};

/**
 * Secondary metadata such as file type, size or upload status. Switches to the error colour with
 * the parent's `error` state, so keep the failure reason in here rather than relying on that colour
 * to carry the meaning on its own.
 */
const AttachmentDescription = ({ className, ...props }: ComponentProps<'span'>) => {
  return (
    <span
      data-slot="attachment-description"
      className={cn(
        'mt-0.5 block min-w-0 max-w-full truncate',
        'text-muted-foreground text-xs',
        'group-data-[state=error]/attachment:text-error/80',
        className
      )}
      {...props}
    />
  );
};

/**
 * Container for one or more actions, aligned to the end of the attachment. On a vertical card it
 * floats over the media instead. Sits above `AttachmentTrigger` so both stay separately clickable.
 */
const AttachmentActions = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="attachment-actions"
      className={cn(
        'relative z-20 flex shrink-0 items-center',
        // Vertical cards have no room beside the media, so the actions float over it.
        'group-data-[orientation=vertical]/attachment:absolute',
        'group-data-[orientation=vertical]/attachment:top-3 group-data-[orientation=vertical]/attachment:right-3',
        'group-data-[orientation=vertical]/attachment:gap-1',
        className
      )}
      {...props}
    />
  );
};

/**
 * An action button. Renders a {@link ButtonV2} and accepts all of its props, defaulting to the
 * `ghost` variant at `icon-xs`.
 *
 * These are icon-only, so give each one an `aria-label` naming the action and its target, e.g.
 * `aria-label="Remove sales-dashboard.pdf"`.
 */
const AttachmentAction = ({ variant = 'ghost', size = 'icon-xs', ...props }: ComponentProps<typeof ButtonV2>) => {
  return <ButtonV2 data-slot="attachment-action" variant={variant} size={size} {...props} />;
};

const attachmentOverlayStyles = cn('absolute inset-0 z-10 outline-none');

/**
 * A full-card overlay that activates the attachment. Sits above the media but below the actions, so
 * the remove button stays clickable.
 *
 * A real `<button>`, for when activating the card runs code. Use {@link AttachmentLink} when it
 * should follow a real link instead.
 *
 * It covers the card with no text of its own, so give it an `aria-label` describing what activating
 * it does.
 */
const AttachmentTrigger = ({ className, type = 'button', ...props }: ComponentProps<'button'>) => {
  return (
    <button data-slot="attachment-trigger" type={type} className={cn(attachmentOverlayStyles, className)} {...props} />
  );
};

/**
 * The anchor form of {@link AttachmentTrigger}, for when activating the card should follow a real
 * link rather than run a handler.
 *
 * The overlay has no visible text, so `children` is the accessible name and is rendered visually
 * hidden. An anchor needs real content, so this is not interchangeable with an `aria-label`.
 *
 * Prefer {@link AttachmentTrigger} whenever the click runs code, and in particular for presigned
 * URLs that expire: a real link lets the browser reach the raw `href` through "open in new tab" or
 * "save link as", bypassing any refresh the handler would have done.
 */
export type AttachmentLinkProps = ComponentProps<'a'> & {
  /** Required: an overlay with nowhere to go is a focus trap with no purpose. */
  href: string;
};

const AttachmentLink = ({ className, children, ...props }: AttachmentLinkProps) => {
  return (
    <a data-slot="attachment-link" className={cn(attachmentOverlayStyles, className)} {...props}>
      <span className="sr-only">{children}</span>
    </a>
  );
};

/**
 * Lays attachments out in a horizontally scrollable, snapping row.
 *
 * Keyboard users reach off-screen items by tabbing to a trigger or action. For a row of
 * presentational attachments with neither, make the group itself reachable with `tabIndex={0}`.
 */
const AttachmentGroup = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div
      data-slot="attachment-group"
      className={cn(
        'flex min-w-0 gap-3 py-1',
        'snap-x snap-mandatory scroll-px-1 overflow-x-auto overscroll-x-contain',
        '*:data-[slot=attachment]:flex-none *:data-[slot=attachment]:snap-start',
        className
      )}
      {...props}
    />
  );
};

export {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentGroup,
  AttachmentLink,
  AttachmentMedia,
  AttachmentTitle,
  AttachmentTrigger,
};
